class VolumeDiscount extends HTMLElement {
  /** @type {{ popular: Record<string, string>, best: Record<string, string> }} */
  static BADGE_LABELS = {
    popular: { de: 'am beliebtesten', en: 'most popular', fr: 'le plus populaire', it: 'più popolare', es: 'más popular' },
    best: { de: 'bester Wert', en: 'best value', fr: 'meilleur rapport', it: 'miglior valore', es: 'mejor valor' }
  };

  constructor() {
    super();
    this.bundlerLoaded = false;
    this.checkInterval = null;
    this.mutationObserver = null;
    this.isProcessing = false;
    this.eventListenersAdded = false;
    this.currentLang = String(document.documentElement.lang || (typeof Shopify !== 'undefined' && Shopify.locale) || 'de').split('-')[0];
  }

  /** @param {any} value */
  t(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    const lang = String(this.currentLang);
    return value[lang] || value.de || Object.values(value)[0] || '';
  }

  connectedCallback() {
    // Immediately disable scroll animations on parent to prevent reset on scroll
    const parent = this.closest('.scroll-trigger');
    if (parent) {
      parent.classList.add('scroll-trigger--cancel');
      parent.classList.remove('scroll-trigger--offscreen');
    }

    this.checkForBundler();
  }

  checkForBundler() {
    const bundlerTarget = this.querySelector('.bundler-target-element-volume-discounts');

    if (bundlerTarget && bundlerTarget.querySelector('.bndlr-volume-discounts')) {
      this.initVolumeDiscounts();
    } else {
      // Check every 500ms for bundler app to load
      this.checkInterval = setInterval(() => {
        const target = this.querySelector('.bundler-target-element-volume-discounts');
        if (target && target.querySelector('.bndlr-volume-discounts')) {
          clearInterval(this.checkInterval);
          this.initVolumeDiscounts();
        }
      }, 500);

      // Stop checking after 10 seconds
      setTimeout(() => {
        if (this.checkInterval) {
          clearInterval(this.checkInterval);
        }
      }, 10000);
    }
  }

  initVolumeDiscounts() {
    if (!this.bundlerLoaded) {
      this.classList.add('bundler-loaded');
      this.bundlerLoaded = true;
      this.setupMutationObserver();
    }

    const discountElements = this.querySelectorAll('[data-quantity]');
    const quantityInput = document.querySelector('.quantity-selector-wrapper [name="quantity"]');

    if (!quantityInput) {
      return;
    }

    // Add images to discount elements
    this.addImagesToDiscounts(discountElements);

    // Only add event listeners once
    if (this.eventListenersAdded) {
      return;
    }
    this.eventListenersAdded = true;

    discountElements.forEach((element) => {
      // Make elements tappable on iOS
      element.style.cursor = 'pointer';

      const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const quantity = element.dataset.quantity;

        // Remove active class from all discount options
        discountElements.forEach(el => el.classList.remove('active'));

        // Add active class to clicked option
        element.classList.add('active');

        // Update quantity input value
        quantityInput.value = quantity;

        // Set the value property directly (iOS Safari fix)
        quantityInput.setAttribute('value', quantity);

        // Create native-like events for better iOS compatibility
        try {
          const inputEvent = new InputEvent('input', { bubbles: true, cancelable: true });
          quantityInput.dispatchEvent(inputEvent);
        } catch (e) {
          quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        }

        try {
          const changeEvent = new Event('change', { bubbles: true, cancelable: true });
          quantityInput.dispatchEvent(changeEvent);
        } catch (e) {
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Manually trigger any Shopify quantity handlers
        if (typeof quantityInput.onchange === 'function') {
          quantityInput.onchange();
        }
        if (typeof quantityInput.oninput === 'function') {
          quantityInput.oninput();
        }
      };

      element.addEventListener('touchstart', handleClick, { passive: false });
      element.addEventListener('click', handleClick);
    });

    // Set first option as active by default
    if (discountElements.length > 0) {
      discountElements[0].classList.add('active');
    }
  }

  setupMutationObserver() {
    // Watch for DOM changes in the bundler container
    const bundlerTarget = this.querySelector('.bundler-target-element-volume-discounts');
    if (!bundlerTarget) {
      return;
    }

    let debounceTimer = null;

    this.mutationObserver = new MutationObserver(() => {
      // Clear previous debounce timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      // Debounce to wait for all Bundler changes to complete
      debounceTimer = setTimeout(() => {
        // Prevent infinite loop
        if (this.isProcessing) {
          return;
        }

        // Check if bundler content was actually replaced (not our own changes)
        const hasDiscountElements = bundlerTarget.querySelector('.bndlr-volume-discounts');
        const hasCustomization = bundlerTarget.querySelector('.l-label');

        if (hasDiscountElements && !hasCustomization) {
          this.isProcessing = true;
          this.eventListenersAdded = false;

          // Disconnect observer before re-applying
          this.mutationObserver.disconnect();

          this.initVolumeDiscounts();
          this.isProcessing = false;

          // Reconnect observer after a short delay
          setTimeout(() => {
            this.mutationObserver.observe(bundlerTarget, {
              childList: true,
              subtree: true
            });
          }, 500);
        }
      }, 100); // Wait 100ms for Bundler to finish all changes
    });

    this.mutationObserver.observe(bundlerTarget, {
      childList: true,
      subtree: true
    });
  }

  addImagesToDiscounts(discountElements) {
    const imageSource = this.querySelector('#VolumeDiscountImages') || this;
    const popularItem = parseInt(imageSource.dataset.popularItem, 10);
    const bestItem = parseInt(imageSource.dataset.bestItem, 10);
    discountElements.forEach((element, index) => {
      const imageIndex = index + 1;
      const imageUrl = imageSource.dataset[`image-${imageIndex}`];

      const mainText = element.querySelector('.bndlr-volume-main-text')?.textContent || '';
      const qty = parseInt(mainText.replace(/\D/g, ''), 10);
      const itemIndex = qty ? Math.round(qty / 30) : imageIndex;
      const item = window.volumeDiscountDecorator?.data?.[`item${itemIndex}`] || null;

      // Skip if already restructured
      if (element.querySelector('.l-label')) {
        return;
      }

      // Mark element as being customized to prevent bundler from touching it
      element.setAttribute('data-customized', 'true');

      // Create label span
      const label = document.createElement('span');
      label.className = 'l-label';

      // Create image container
      const imgContainer = document.createElement('div');
      imgContainer.className = 'l-img';

      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = `Volume discount ${imageIndex}`;
        imgContainer.appendChild(img);
      }

      const textContainer = document.createElement('div');
      textContainer.className = 'l-text';

      const priceContainer = document.createElement('div');
      priceContainer.className = 'l-price';

      while (element.firstChild) {
        textContainer.appendChild(element.firstChild);
      }

      const mainTextEl = textContainer.querySelector('.bndlr-volume-main-text');
      if (mainTextEl && item?.text1) {
        mainTextEl.textContent = this.t(item.text1);
      }

      const priceEl = textContainer.querySelector('.bndlr-volume-variant-price');
      const savingEl = textContainer.querySelector('.bndlr-volume-saving-text');
      if (priceEl) {
        priceContainer.appendChild(priceEl);
        const itemQty = item?.quantity;
        if (itemQty && itemQty > 1) {
          ['.bndlr-old-price', '.bndlr-new-price'].forEach((sel) => {
            const el = priceEl.querySelector(sel);
            if (!el) return;
            el.textContent = el.textContent.replace(/\d+[.,]\d+/, (match) => {
              const num = parseFloat(match.replace(',', '.'));
              return (Math.floor(num * itemQty * 10) / 10).toFixed(2).replace('.', match.includes(',') ? ',' : '.');
            });
          });
        }
      }
      if (savingEl) {
        priceContainer.appendChild(savingEl);
        const parts = savingEl.textContent.split(';');
        if (parts.length > 1) {
          savingEl.textContent = parts.slice(1).join(';').trim();
          const youSave = document.createElement('span');
          youSave.className = `you-save you-save-${imageIndex}`;
          youSave.textContent = parts[0].trim();
          priceContainer.appendChild(youSave);
        }
      }

      if (item?.text2) {
        const text2 = document.createElement('span');
        text2.textContent = this.t(item.text2);
        textContainer.appendChild(text2);
      }

      const benefitsContainer = document.createElement('div');
      benefitsContainer.className = 'l-benefits';
      ['benefit1', 'benefit2', 'benefit3'].forEach((key) => {
        if (item?.benefits?.[key]) {
          const b = document.createElement('span');
          b.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>${this.t(item.benefits[key])}`;
          benefitsContainer.appendChild(b);
        }
      });
      textContainer.appendChild(benefitsContainer);

      if (imageIndex === popularItem) {
        const popularBadge = document.createElement('span');
        popularBadge.textContent = this.t(VolumeDiscount.BADGE_LABELS.popular);
        popularBadge.className = 'popular-badge';
        element.appendChild(popularBadge);
      }

      if (imageIndex === bestItem) {
        const bestBadge = document.createElement('span');
        bestBadge.textContent = this.t(VolumeDiscount.BADGE_LABELS.best);
        bestBadge.className = 'best-badge';
        element.appendChild(bestBadge);
      }

      element.appendChild(label);
      element.appendChild(imgContainer);
      element.appendChild(textContainer);
      element.appendChild(priceContainer);
    });
  }

  disconnectedCallback() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }
}

customElements.define('volume-discount', VolumeDiscount);